
import React from 'react';
import { Box, Typography, Button, Container, Paper, useTheme, useMediaQuery, Link } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import HomeIcon from '@mui/icons-material/Home';

const UnauthorizedPage: React.FC = () => {
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  return (
    <Box
      sx={{
        height: '100vh',
        width: '100vw',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        background: 'linear-gradient(135deg, #fdfbfb 0%, #ebedee 100%)',
        position: 'fixed',
        top: 0,
        left: 0,
        zIndex: 9999,
        overflow: 'hidden',
      }}
    >
      {/* Decorative Background Elements */}
      <Box
        sx={{
          position: 'absolute',
          width: '40vw',
          height: '40vw',
          borderRadius: '50%',
          background: 'radial-gradient(circle, rgba(94, 142, 126, 0.1) 0%, rgba(94, 142, 126, 0) 70%)',
          top: '-10%',
          right: '-10%',
          zIndex: 0,
        }}
      />
      <Box
        sx={{
          position: 'absolute',
          width: '30vw',
          height: '30vw',
          borderRadius: '50%',
          background: 'radial-gradient(circle, rgba(26, 35, 126, 0.05) 0%, rgba(26, 35, 126, 0) 70%)',
          bottom: '-5%',
          left: '-5%',
          zIndex: 0,
        }}
      />

      <Container maxWidth="sm" sx={{ position: 'relative', zIndex: 1 }}>
        <Paper
          elevation={0}
          sx={{
            p: { xs: 4, md: 8 },
            borderRadius: 6,
            textAlign: 'center',
            backgroundColor: 'rgba(255, 255, 255, 0.7)',
            backdropFilter: 'blur(20px)',
            boxShadow: '0 20px 50px rgba(0, 0, 0, 0.05)',
            border: '1px solid rgba(255, 255, 255, 0.3)',
            animation: 'fadeInUp 0.8s ease-out forwards',
            '@keyframes fadeInUp': {
              '0%': {
                opacity: 0,
                transform: 'translateY(30px)',
              },
              '100%': {
                opacity: 1,
                transform: 'translateY(0)',
              },
            },
          }}
        >
          <Box
            sx={{
              display: 'inline-flex',
              p: 2.5,
              borderRadius: '24px',
              backgroundColor: '#fff',
              boxShadow: '0 10px 30px rgba(211, 47, 47, 0.1)',
              color: '#d32f2f',
              mb: 4,
              animation: 'pulse 2s infinite',
              '@keyframes pulse': {
                '0%': { transform: 'scale(1)', boxShadow: '0 0 0 0 rgba(211, 47, 47, 0.4)' },
                '70%': { transform: 'scale(1.05)', boxShadow: '0 0 0 15px rgba(211, 47, 47, 0)' },
                '100%': { transform: 'scale(1)', boxShadow: '0 0 0 0 rgba(211, 47, 47, 0)' },
              },
            }}
          >
            <LockOutlinedIcon sx={{ fontSize: 60 }} />
          </Box>

          <Typography
            variant={isMobile ? "h4" : "h3"}
            component="h1"
            gutterBottom
            sx={{ 
              fontWeight: 800, 
              color: '#000', 
              mb: 2,
              letterSpacing: '-0.02em'
            }}
          >
            Access Denied
          </Typography>

          <Typography
            variant="body1"
            sx={{ 
              color: '#000', 
              mb: 5, 
              fontSize: '1.15rem', 
              lineHeight: 1.6,
              maxWidth: '90%',
              mx: 'auto'
            }}
          >
            Oops! It looks like you've wandered into a restricted area. 
            You don't have the necessary permissions to view this page.
          </Typography>

          <Box 
            sx={{ 
              display: 'flex', 
              flexDirection: isMobile ? 'column' : 'row',
              gap: 2, 
              justifyContent: 'center' 
            }}
          >
            <Button
              variant="outlined"
              startIcon={<ArrowBackIcon />}
              onClick={() => navigate(-1)}
              sx={{
                borderRadius: '12px',
                px: 4,
                py: 1.5,
                textTransform: 'none',
                fontWeight: 600,
                fontSize: '1rem',
                borderWidth: '2px',
                '&:hover': {
                  borderWidth: '2px',
                },
              }}
            >
              Go Back
            </Button>
            <Button
              variant="contained"
              startIcon={<HomeIcon />}
              onClick={() => navigate('/dashboard')}
              sx={{
                borderRadius: '12px',
                px: 4,
                py: 1.5,
                textTransform: 'none',
                fontWeight: 600,
                fontSize: '1rem',
                backgroundColor: '#5E8E7E',
                boxShadow: '0 8px 20px rgba(94, 142, 126, 0.3)',
                '&:hover': {
                  backgroundColor: '#4a7164',
                  boxShadow: '0 10px 25px rgba(94, 142, 126, 0.4)',
                },
              }}
            >
              Back to Dashboard
            </Button>
          </Box>

          <Typography
            variant="body2"
            sx={{ 
              mt: 4, 
              color: '#666',
              fontSize: '0.95rem'
            }}
          >
            Need access? {' '}
            <Link 
              component="button" 
              onClick={() => navigate('/contact')}
              sx={{ 
                color: '#5E8E7E', 
                fontWeight: 700, 
                textDecoration: 'none',
                verticalAlign: 'baseline',
                fontSize: 'inherit',
                '&:hover': {
                  textDecoration: 'underline'
                }
              }}
            >
              Contact Support
            </Link>
          </Typography>

          <Typography
            variant="caption"
            sx={{ 
              display: 'block',
              mt: 6,
              color: '#9fa8da',
              fontWeight: 500,
              textTransform: 'uppercase',
              letterSpacing: '0.1em'
            }}
          >
            TalentHub Security Protocol
          </Typography>
        </Paper>
      </Container>
    </Box>
  );
};

export default UnauthorizedPage;

